import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
// Plug, Plus and UserPlus are parked with the side panel.
import { ChevronDown, ChevronUp } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import type { ClientPerson, ClientPersonCard, MyPart as MyPartData, OrbitAsk } from '../../types/aai';
import { Chip, Loading } from '../../components/aa';
import { OrbitDeck } from '../../components/orbit-deck/OrbitDeck';
import type { DeckAnswer, DeckCardModel } from '../../components/orbit-deck/types';
import { transcribeFor } from '../orbit-deck/deckModel';
import { originLine } from './roomCopy';
import MemberIntake from '../../components/intake/MemberIntake';

/*
 * A person's own part, for a company whose Solution Partner runs the journey.
 *
 * The partner walks the journey; the people at the company each have a part in
 * it, and that is the whole screen: the cards they answered about their own
 * work (held, and still to answer), what their role does next, and their work.
 *
 *   Owner, AI Champion   the company's cards, its departments, its tools
 *   Department head      their department's cards, their team, its tools
 *   Everyone else        their cards and their work
 */

const INPUT_OF: Record<string, DeckCardModel['input']> = {
  select: 'choose', multiselect: 'choose_many', number: 'number', text: 'text', longtext: 'say',
};

const errorText = (err: unknown, fallback: string): string => {
  const e = err as { response?: { data?: { detail?: string } } };
  return e.response?.data?.detail || fallback;
};

const cardFor = (person: ClientPerson, card: ClientPersonCard, block: string, round: number): DeckCardModel => {
  const picked = Array.isArray(card.value) ? (card.value as string[]) : [String(card.value ?? '')];
  let tagLabel = block;
  if (!card.answered && card.skipped) tagLabel = `Skipped earlier · ${block}`;
  if (card.answered && card.by === 'partner') tagLabel = `Filled by ${card.filled_by || 'your partner'} · ${block}`;
  if (card.answered && card.by === 'person') tagLabel = `Your answer · ${block}`;
  return {
    id: `${person.id}:${round}:${card.key}`,
    tag: card.answered ? 'confirm' : 'ask',
    tagLabel,
    title: card.label,
    why: card.why,
    input: INPUT_OF[card.kind] || 'say',
    options: card.options.map((o) => ({
      value: o.value,
      label: o.subtitle ? `${o.label} (${o.subtitle})` : o.label,
      detected: card.answered && picked.includes(o.value),
    })),
    placeholder: card.placeholder,
    prefill: card.answered && !card.options.length ? String(card.value ?? '') : undefined,
    detectedNote: 'Your answer is switched on. Tap to change it.',
    fieldKey: card.key,
  };
};

/* Their own cards, as two decks: what they already answered (or was filled in
   for them), and what is still to answer. */
const MyCards: React.FC<{ clientId: string; person: ClientPerson; onChanged: (next: ClientPerson) => void }> = ({ clientId, person, onChanged }) => {
  const flat = person.blocks.flatMap((b) => b.cards.map((c) => ({ card: c, block: b.title })));
  const heldCount = flat.filter(({ card }) => card.answered).length;
  const openCount = flat.filter(({ card }) => !card.answered).length;
  const [tab, setTab] = useState<'held' | 'open'>(openCount > 0 ? 'open' : 'held');
  const [round, setRound] = useState(0);
  const [through, setThrough] = useState(false);
  const transcribe = useMemo(() => transcribeFor(clientId), [clientId]);

  /* Skipped ones stay to answer, after the rest, as the deck deals them. */
  const cards = useMemo(() => flat
    .filter(({ card }) => (tab === 'held' ? card.answered : !card.answered))
    .sort((a, b) => Number(!!a.card.skipped) - Number(!!b.card.skipped))
    .map(({ card, block }) => cardFor(person, card, block, round)),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [person.id, tab, round]);

  const answer = async (card: DeckCardModel, value: DeckAnswer) => {
    try {
      const r = await aaiApi.answerMyPart({ key: card.fieldKey || '', value: value.value }, clientId);
      onChanged(r.data);
    } catch (err) {
      toast.error(errorText(err, 'Could not save that answer.'));
      throw new Error('answer failed');
    }
  };
  const skip = async (card: DeckCardModel) => {
    if (tab === 'held') return;
    try {
      const r = await aaiApi.answerMyPart({ key: card.fieldKey || '', skipped: true }, clientId);
      onChanged(r.data);
    } catch {
      toast.error('Could not skip that one.');
    }
  };

  const pick = (next: 'held' | 'open') => { setTab(next); setRound((n) => n + 1); setThrough(false); };

  return (
    <div>
      <div className="mb-4 flex w-max overflow-hidden rounded-full border border-line bg-white p-0.5" role="tablist" aria-label="Your cards">
        {([['held', 'held', heldCount], ['open', 'to answer', openCount]] as const).map(([id, word, n]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => pick(id)}
            className={`rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold transition-colors ${tab === id ? 'bg-ink text-white' : 'text-ink-muted hover:text-ink'}`}>
            <span className="tabular-nums">{n}</span> {word}
          </button>
        ))}
      </div>
      {cards.length > 0 && !through && (
        <OrbitDeck
          key={`${tab}:${round}`}
          cards={cards}
          onAnswer={answer}
          onSkip={skip}
          onDocument={async () => { toast.info('Here, answer or skip each card.'); return undefined; }}
          onDone={() => setThrough(true)}
          transcribe={transcribe}
          labels={{
            deckTitle: tab === 'held' ? 'Your answers' : 'To answer',
            primary: 'Save',
            skip: tab === 'held' ? 'Leave it as it is' : 'Skip this one',
            skipWord: tab === 'held' ? 'next' : 'skip',
          }}
        />
      )}
      {(cards.length === 0 || through) && (
        <div className="rounded-[14px] border border-line bg-[var(--aa-card)] px-5 py-4 text-[13px] text-ink">
          {tab === 'open' && openCount === 0 && 'Nothing left to answer. Thank you.'}
          {tab === 'open' && openCount > 0 && `${openCount} still to answer. Skipped ones wait here.`}
          {tab === 'held' && heldCount === 0 && 'Nothing answered yet.'}
          {tab === 'held' && heldCount > 0 && 'That is everything you answered.'}
          {cards.length > 0 && (
            <button type="button" className="ml-2 font-semibold underline" onClick={() => { setRound((n) => n + 1); setThrough(false); }}>
              Go through them again
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const ASK_INPUT: Record<OrbitAsk['kind'], DeckCardModel['input']> = {
  say: 'say', choose: 'choose', choose_many: 'choose_many', number: 'number', people: 'people',
};

type Held = NonNullable<MyPartData['cards']>['held'][number];

/* The cards waiting on them, whoever set them: the company's, their
   department's, the partner's own intakes. What Orbit already holds is its own
   deck, prefilled, to confirm or put right. */
const TheirCards: React.FC<{
  clientId: string; title: string; asks: OrbitAsk[]; held: Held[]; onChanged: () => void;
  /** The cards to answer were worked through, here, in front of them. */
  onFinished?: () => void;
}> = ({ clientId, title, asks, held, onChanged, onFinished }) => {
  const [tab, setTab] = useState<'open' | 'held'>(asks.length > 0 ? 'open' : 'held');
  const [round, setRound] = useState(0);
  const [through, setThrough] = useState(false);
  const transcribe = useMemo(() => transcribeFor(clientId), [clientId]);
  /* Answering the last card reloads the list empty, and the deck goes before
     it can say it is done: that counts as finishing too. */
  const answeredHere = useRef(false);
  const told = useRef(false);
  const finish = useCallback(() => {
    if (told.current || !onFinished) return;
    told.current = true;
    onFinished();
  }, [onFinished]);
  useEffect(() => {
    if (answeredHere.current && asks.length === 0) finish();
  }, [asks.length, finish]);

  const askCards = useMemo<DeckCardModel[]>(() => asks.map((ask) => ({
    id: ask.id,
    tag: ask.origin === 'orbit_reading' ? 'confirm' : 'ask',
    tagLabel: ask.later ? `Skipped earlier · ${ask.intake_label || originLine(ask)}` : ask.intake_label || originLine(ask),
    title: ask.question,
    why: ask.why,
    input: ASK_INPUT[ask.kind] || 'say',
    options: ask.options,
    meta: { askId: ask.id },
  })), [asks]);

  const heldCards = useMemo<DeckCardModel[]>(() => held.map((f) => ({
    id: `held:${round}:${f.scope}:${f.field_key}`,
    tag: 'confirm',
    tagLabel: `${f.state === 'confirm' ? 'To confirm' : 'Held'} · ${f.group}`,
    title: f.label,
    why: f.state === 'confirm' ? 'Orbit read this. Keep it if it is right, or put it right.' : 'Already on file. Change it if it has moved on.',
    input: 'confirm',
    facts: [{ field_key: f.field_key, label: f.label, value: f.value, value_display: f.value_display || 'nothing recorded',
              source: f.source, confidence: f.confidence, scope: f.scope }],
  })), [held, round]);

  const answer = async (card: DeckCardModel, value: DeckAnswer) => {
    try {
      await aaiApi.answerOrbitAsk(card.id, value.value);
      answeredHere.current = true;
      onChanged();
    } catch (err) {
      toast.error(errorText(err, 'Could not save that answer.'));
      throw new Error('answer failed');
    }
  };
  const skip = async (card: DeckCardModel) => {
    if (tab === 'held') return;
    try {
      await aaiApi.skipOrbitAsk(card.id);
      onChanged();
    } catch {
      toast.error('Could not keep that one for later.');
    }
  };
  const confirm = async (_card: DeckCardModel, value: DeckAnswer) => {
    const items = (value.confirm || []).map((d) => ({
      field_key: d.field_key, scope: d.scope || '', correct: d.action === 'correct', skip: d.action === 'skip', value: d.value,
    }));
    if (!items.length) return;
    try {
      await aaiApi.confirmOrbitFacts(items);
      onChanged();
    } catch {
      toast.error('Could not save that.');
      throw new Error('confirm failed');
    }
  };

  const pick = (next: 'open' | 'held') => { setTab(next); setRound((n) => n + 1); setThrough(false); };
  const cards = tab === 'open' ? askCards : heldCards;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {title && <h2 className="m-0 text-[15px] font-bold text-ink">{title}</h2>}
        <div className="flex w-max overflow-hidden rounded-full border border-line bg-white p-0.5" role="tablist" aria-label={title}>
          {([['open', 'to answer', asks.length], ['held', 'held', held.length]] as const).map(([id, word, n]) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => pick(id)}
              className={`rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold transition-colors ${tab === id ? 'bg-ink text-white' : 'text-ink-muted hover:text-ink'}`}>
              <span className="tabular-nums">{n}</span> {word}
            </button>
          ))}
        </div>
      </div>
      {cards.length > 0 && !through && (
        <OrbitDeck
          key={`${tab}:${round}`}
          cards={cards}
          onAnswer={tab === 'open' ? answer : confirm}
          onSkip={skip}
          onDocument={async () => { toast.info('Here, answer or skip each card.'); return undefined; }}
          onDone={() => {
            setThrough(true);
            if (tab === 'open') finish();
          }}
          transcribe={transcribe}
          labels={{
            deckTitle: tab === 'open' ? 'To answer' : 'Held',
            primary: tab === 'open' ? 'Answer' : 'Save',
            skip: tab === 'open' ? 'Later' : 'Leave it as it is',
            skipWord: tab === 'open' ? 'later' : 'next',
          }}
        />
      )}
      {(cards.length === 0 || through) && (
        <div className="rounded-[14px] border border-line bg-[var(--aa-card)] px-5 py-4 text-[13px] text-ink">
          {tab === 'open' && cards.length === 0 && 'Nothing waiting on you. Thank you.'}
          {tab === 'open' && cards.length > 0 && `${cards.length} still to answer. Skipped ones wait here.`}
          {tab === 'held' && cards.length === 0 && 'Orbit holds nothing here yet.'}
          {tab === 'held' && cards.length > 0 && 'That is everything held here.'}
          {cards.length > 0 && (
            <button type="button" className="ml-2 font-semibold underline" onClick={() => { setRound((n) => n + 1); setThrough(false); }}>
              Go through them again
            </button>
          )}
        </div>
      )}
    </div>
  );
};

type Group = NonNullable<NonNullable<MyPartData['cards']>['groups']>[number];

/* Their part as the journey's own steps: one row per intake, collapsed, each
   saying how much is waiting, opening onto its cards. One open at a time. */
// Parked: the dashboard shows the partner's intake sections instead.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Steps: React.FC<{
  clientId: string;
  groups: Group[];
  person: ClientPerson | null;
  onChanged: () => void;
  onPerson: (next: ClientPerson) => void;
  onView: (next: MyPartData) => void;
  /** The company side closes steps on the client's side of the journey. */
  runsJourney: boolean;
}> = ({ clientId, groups, person, onChanged, onPerson, onView, runsJourney }) => {
  const personCards = (person?.blocks || []).flatMap((b) => b.cards);
  const countsOf = (g: Group) => {
    if (g.key !== 'team') return { open: g.asks.length, held: g.held.length };
    return {
      open: personCards.filter((c) => !c.answered).length,
      held: personCards.filter((c) => c.answered).length,
    };
  };
  const first = groups.find((g) => g.next) || groups.find((g) => countsOf(g).open > 0);
  const [openKey, setOpenKey] = useState<string | null>(first ? first.key : null);

  const finishStep = async (g: Group) => {
    try {
      const r = await aaiApi.finishMyPart(g.key, clientId);
      onView(r.data);
      toast.success(`${g.title} is done.`);
    } catch {
      toast.error('Could not close that step. Your partner can mark it done.');
    }
  };
  const canClose = (g: Group) => !g.done && g.key !== 'team' && (runsJourney || g.key === 'frame' || g.key === 'tools' || g.key.startsWith('intake:'));

  if (!groups.length) {
    return (
      <div className="rounded-[14px] border border-line bg-[var(--aa-card)] px-5 py-4 text-[13px] text-ink">
        Nothing waiting on you yet. When your partner or your Owner adds you, your cards appear here.
      </div>
    );
  }

  return (
    <div className="path">
      {groups.map((g, i) => {
        const { open, held } = countsOf(g);
        const shown = openKey === g.key;
        let state = '';
        let side = 'Nothing yet';
        if (held > 0) { state = 'done'; side = 'Answered'; }
        if (open > 0) { state = 'now'; side = `${open} to answer`; }
        if (g.next) { state = 'now'; side = open > 0 ? `Now · ${open} to answer` : 'Now'; }
        if (g.done && g.key !== 'team' && open === 0) { state = 'done'; side = 'Done'; }
        let empty = 'No cards for you here';
        if (g.key === 'departments') empty = 'Each department’s head answers its cards. Add your departments in Your company.';
        const line = [open > 0 ? `${open} to answer` : '', held > 0 ? `${held} held` : ''].filter(Boolean).join(' · ') || empty;
        return (
          <div key={g.key}>
            <button
              type="button"
              aria-expanded={shown}
              onClick={() => setOpenKey(shown ? null : g.key)}
              className={`phase ${state} w-full cursor-pointer text-left transition-shadow hover:shadow-sm`}
            >
              <span className="n">{state === 'done' ? '✓' : i + 1}</span>
              <div>
                <strong>{g.title}</strong>
                {g.purpose && <span className="who">{g.purpose}</span>}
                <span className="prog">{line}</span>
              </div>
              <div className="side">
                <em>{side}</em>
                <span className="inline-flex items-center gap-1 rounded-full border border-line-strong bg-white px-3 py-1.5 text-[11.5px] font-semibold text-ink">
                  {shown ? 'Close' : 'Open'}
                  {shown ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                </span>
              </div>
            </button>
            {shown && (
              <div className="mb-2 mt-3 pl-0 sm:pl-[50px]">
                {g.key === 'team' && person && <MyCards clientId={clientId} person={person} onChanged={onPerson} />}
                {g.key === 'team' && !person && (
                  <div className="rounded-[14px] border border-line bg-[var(--aa-card)] px-5 py-4 text-[13px] text-ink">
                    You have no Person intake here yet. When your partner or your Owner adds you, your cards appear here.
                  </div>
                )}
                {/* Parked: a manual "Mark done". Every intake stays open; a step
                    reads Answered once its cards are, and its answers stay held. */}
                {g.key !== 'team' && (g.asks.length > 0 || g.held.length > 0) && (
                  <TheirCards
                    clientId={clientId}
                    title=""
                    asks={g.asks}
                    held={g.held}
                    onChanged={onChanged}
                    onFinished={canClose(g) ? () => { finishStep(g); } : undefined}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- parked with the side panel
const Box: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="rounded-[14px] border border-line bg-white p-4">
    <div className="mb-2.5 text-[13px] font-semibold text-ink">{title}</div>
    {children}
  </div>
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- parked with the side panel
const Chips: React.FC<{ items: string[]; empty: string }> = ({ items, empty }) => {
  if (!items.length) return <p className="m-0 text-[12.5px] leading-[19px] text-ink-dim">{empty}</p>;
  return <div className="flex flex-wrap gap-1.5">{items.map((t) => <Chip key={t} tone="neutral">{t}</Chip>)}</div>;
};

export const MyPart: React.FC<{
  clientId: string;
  /** Without it the part sits on a Home under that panel's own head: no back, no title. */
  onBack?: () => void;
  backLabel?: string;
  /** Their department's cards, by slug. */
  onOpenDepartment: (slug: string) => void;
  onAddDepartment: () => void;
  onInvite: () => void;
  onOpenTools: () => void;
  onOpenWork: (link: string) => void;
}> = ({ clientId, onBack, backLabel, onOpenDepartment, onAddDepartment, onInvite, onOpenTools, onOpenWork }) => {
  const [data, setData] = useState<MyPartData | null>(null);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try {
      const r = await aaiApi.myPart(clientId);
      setData(r.data);
    } catch (err) {
      setError(errorText(err, 'Could not load your part.'));
    }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="text-[13px] text-danger">{error}</div>;
  if (!data) return <Loading label="Loading your part" />;

  // Parked with the side panel.
  // const off = new Set(data.intakes_off || []);
  // Parked with the old intake steps.
  // const groups = data.cards?.groups || [];

  return (
    <>
      {onBack && (
        <div className="det-head">
          <button type="button" className="back" onClick={onBack}>← {backLabel}</button>
          <h1>Your part</h1>
        </div>
      )}
      <div className="fill-wrap">
        <p className="fill-purpose">
          {!data.house && 'Your partner runs the engagement. '}<b>This is your part of it:</b> the cards waiting on you, and what
          Orbit already holds for you to confirm.
        </p>
        <div className="mx-auto w-full max-w-[880px]">
          <div className="min-w-0">
            {/* Replaced by the partner's intake sections, the same for every role.
            <Steps
              clientId={clientId}
              groups={groups}
              person={data.intake}
              onChanged={load}
              onPerson={(next) => setData({ ...data, intake: next })}
              onView={setData}
              runsJourney={data.kind === 'company'}
            />
            */}
            <MemberIntake clientId={clientId} />
          </div>
          {/* Parked for now: the side panel, Your company and Your department.
          <div className="fill-aside w-full shrink-0 lg:w-[360px]">
            {data.kind === 'company' && data.company && (
              <Box title="Your company">
                {!off.has('department') && (
                  <div className="mb-4">
                    <div className="mb-1 text-[12px] font-semibold text-ink">Departments</div>
                    <Chips items={data.company.departments.map((d) => d.name)} empty="No departments yet." />
                    <button type="button" onClick={onAddDepartment}
                      className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-ink-muted hover:text-ink">
                      <Plus size={12} /> Add a department
                    </button>
                  </div>
                )}
                {!off.has('tool') && (
                  <>
                    <div className="mb-1 text-[12px] font-semibold text-ink">Tools</div>
                    <Chips items={data.company.tools} empty="No tools recorded yet." />
                    <button type="button" onClick={onOpenTools}
                      className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-ink-muted hover:text-ink">
                      <Plug size={12} /> Enable more tools
                    </button>
                  </>
                )}
                {off.has('business') && off.has('department') && off.has('tool') && (
                  <p className="m-0 text-[12.5px] leading-[19px] text-ink-dim">Your partner&rsquo;s intake asks nothing more of the company here.</p>
                )}
              </Box>
            )}
            {data.kind === 'department' && data.department && (
              <Box title="Your department">
                <div className="mb-4">
                  <div className="mb-1 text-[12px] font-semibold text-ink">Department</div>
                  <Chips items={[data.department.name]} empty="" />
                </div>
                <div className="mb-4">
                  <div className="mb-1 text-[12px] font-semibold text-ink">Team</div>
                  <p className="m-0 text-[12.5px] leading-[19px] text-ink-dim">
                    {data.department.members} {data.department.members === 1 ? 'person' : 'people'} · {data.department.onboarded} onboarded
                  </p>
                  <button type="button" onClick={onInvite}
                    className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-ink-muted hover:text-ink">
                    <UserPlus size={12} /> Invite your team
                  </button>
                </div>
                <div className="mb-1 text-[12px] font-semibold text-ink">Tools</div>
                <Chips items={data.department.tools} empty="No tools recorded for your department yet." />
              </Box>
            )}
            {data.kind === 'department' && !data.department && (
              <Box title="Your department">
                <p className="m-0 text-[12.5px] text-ink-dim">You are not placed in a department yet. Your Owner places you.</p>
              </Box>
            )}
          */}
            {/* Parked: Your work.
            <Box title="Your work">
              {data.work.length === 0 && (
                <p className="m-0 text-[12.5px] leading-[19px] text-ink-dim">
                  Nothing yet. What you ask Orbit for, and the work it becomes, shows here.
                </p>
              )}
              {data.work.length > 0 && (
                <ul className="m-0 list-none space-y-1.5 p-0">
                  {data.work.map((w) => (
                    <li key={w.id}>
                      <button type="button" onClick={() => onOpenWork(w.link)}
                        className="flex w-full items-center gap-2 rounded-md px-1 py-1 text-left text-[12.5px] hover:bg-canvas-inset">
                        <Building2 size={13} className="shrink-0 text-ink-soft" />
                        <span className="min-w-0 flex-1 truncate text-ink">{w.title}</span>
                        <Chip tone="neutral">{w.status}</Chip>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Box>
            */}
        </div>
      </div>
    </>
  );
};

export default MyPart;
